import type { DocumentReference, Transaction } from 'firebase/firestore';
import type {
  DraftSourceRef,
  ExpenseEntryData,
  ManualInvoiceAdjustment,
  MatterDraftInvoiceState,
  TimeEntryData,
} from '@shared/types';
import { cleanDataToFirestore } from './converters';

export interface MatterDraftInvoiceSourceRef extends DraftSourceRef {
  ref: DocumentReference<TimeEntryData | ExpenseEntryData>;
}

export type MatterDraftInvoiceSourceAction =
  | 'remove'
  | 'attach'
  | 'markUnbillable'
  | 'delete';

type ConflictReason =
  | 'missing-source'
  | 'cross-matter'
  | 'assigned'
  | 'unbillable'
  | 'draft-time-entry';

export const mutateMatterDraftInvoiceSourceInTransaction = async (props: {
  transaction: Transaction;
  matterId: string;
  draftStateRef: DocumentReference<MatterDraftInvoiceState>;
  sourceRef: MatterDraftInvoiceSourceRef;
  action: MatterDraftInvoiceSourceAction;
  beforeDelete?: (source: TimeEntryData | ExpenseEntryData) => Promise<void>;
}) => {
  const [stateSnapshot, sourceSnapshot] = await Promise.all([
    props.transaction.get(props.draftStateRef),
    props.transaction.get(props.sourceRef.ref),
  ]);
  const source = sourceSnapshot.data();
  if (!source) return conflict(props, 'missing-source');
  const sourceConflict = getSourceConflict(props, source);
  if (sourceConflict) return sourceConflict;

  const timestamp = new Date();
  const currentState = stateSnapshot.data();
  const refs = (currentState?.removedSourceRefs ?? []).filter(
    (ref) => sourceRefKey(ref) !== sourceRefKey(props.sourceRef),
  );
  const nextState: MatterDraftInvoiceState = {
    createdTimestamp: currentState?.createdTimestamp ?? timestamp,
    updatedTimestamp: timestamp,
    removedSourceRefs:
      props.action === 'remove'
        ? [
            ...refs,
            {
              entryType: props.sourceRef.entryType,
              entryId: props.sourceRef.entryId,
            },
          ]
        : refs,
    manualAdjustments: currentState?.manualAdjustments ?? [],
  };

  if (props.action === 'markUnbillable') {
    props.transaction.update(props.sourceRef.ref, {
      billingState: 'unbillable',
    });
  }
  if (props.action === 'delete') {
    await props.beforeDelete?.(source);
    props.transaction.delete(props.sourceRef.ref);
  }

  writeMatterDraftState({
    transaction: props.transaction,
    ref: props.draftStateRef,
    currentState,
    nextState,
  });

  return {
    status: resultStatus(props.action),
    entryType: props.sourceRef.entryType,
    entryId: props.sourceRef.entryId,
  };
};

export type MatterDraftInvoiceSourceResult = Awaited<
  ReturnType<typeof mutateMatterDraftInvoiceSourceInTransaction>
>;

export type MatterDraftInvoiceAttachSourcesResult =
  | {
      status: 'attached';
      attachedTimeEntryCount: number;
      attachedExpenseEntryCount: number;
    }
  | Extract<MatterDraftInvoiceSourceResult, { status: 'conflict' }>;

export const attachMatterDraftInvoiceSourcesInTransaction = async (props: {
  transaction: Transaction;
  matterId: string;
  draftStateRef: DocumentReference<MatterDraftInvoiceState>;
  sourceRefs: MatterDraftInvoiceSourceRef[];
}): Promise<MatterDraftInvoiceAttachSourcesResult> => {
  const [stateSnapshot, ...sourceSnapshots] = await Promise.all([
    props.transaction.get(props.draftStateRef),
    ...props.sourceRefs.map((sourceRef) =>
      props.transaction.get(sourceRef.ref),
    ),
  ]);
  for (const [index, sourceSnapshot] of sourceSnapshots.entries()) {
    const sourceRef = props.sourceRefs[index];
    const source = sourceSnapshot.data();
    if (!source) return conflict({ sourceRef }, 'missing-source');
    const sourceConflict = getSourceConflict(
      { action: 'attach', matterId: props.matterId, sourceRef },
      source,
    );
    if (sourceConflict) return sourceConflict;
  }

  const timestamp = new Date();
  const currentState = stateSnapshot.data();
  const sourceKeys = new Set(props.sourceRefs.map(sourceRefKey));
  const nextState: MatterDraftInvoiceState = {
    createdTimestamp: currentState?.createdTimestamp ?? timestamp,
    updatedTimestamp: timestamp,
    removedSourceRefs: (currentState?.removedSourceRefs ?? []).filter(
      (ref) => !sourceKeys.has(sourceRefKey(ref)),
    ),
    manualAdjustments: currentState?.manualAdjustments ?? [],
  };
  writeMatterDraftState({
    transaction: props.transaction,
    ref: props.draftStateRef,
    currentState,
    nextState,
  });

  return {
    status: 'attached',
    attachedTimeEntryCount: props.sourceRefs.filter(
      ({ entryType }) => entryType === 'timeEntry',
    ).length,
    attachedExpenseEntryCount: props.sourceRefs.filter(
      ({ entryType }) => entryType === 'expenseEntry',
    ).length,
  };
};

export const updateMatterDraftInvoiceManualAdjustmentsInTransaction =
  async (props: {
    transaction: Transaction;
    matterId: string;
    draftStateRef: DocumentReference<MatterDraftInvoiceState>;
    buildManualAdjustments: (
      manualAdjustments: ManualInvoiceAdjustment[],
    ) => ManualInvoiceAdjustment[];
  }) => {
    const stateSnapshot = await props.transaction.get(props.draftStateRef);
    const currentState = stateSnapshot.data();
    const timestamp = new Date();
    const nextState: MatterDraftInvoiceState = {
      createdTimestamp: currentState?.createdTimestamp ?? timestamp,
      updatedTimestamp: timestamp,
      removedSourceRefs: currentState?.removedSourceRefs ?? [],
      manualAdjustments: props.buildManualAdjustments(
        currentState?.manualAdjustments ?? [],
      ),
    };

    return writeMatterDraftState({
      transaction: props.transaction,
      ref: props.draftStateRef,
      currentState,
      nextState,
    });
  };

export type MatterDraftInvoiceManualAdjustmentsResult = Awaited<
  ReturnType<typeof updateMatterDraftInvoiceManualAdjustmentsInTransaction>
>;

const getSourceConflict = (
  props: {
    action: MatterDraftInvoiceSourceAction;
    matterId: string;
    sourceRef: DraftSourceRef;
  },
  source: TimeEntryData | ExpenseEntryData,
): ReturnType<typeof conflict> | undefined => {
  if (source.matter !== props.matterId) return conflict(props, 'cross-matter');
  if (source.invoiceId !== undefined) return conflict(props, 'assigned');
  if (source.billingState === 'unbillable' && props.action !== 'delete')
    return conflict(props, 'unbillable');
  if (
    props.sourceRef.entryType === 'timeEntry' &&
    props.action !== 'delete' &&
    (source as TimeEntryData).status === 'draft'
  ) {
    return conflict(props, 'draft-time-entry');
  }
};

const conflict = (
  props: { sourceRef: DraftSourceRef },
  reason: ConflictReason,
) =>
  ({
    status: 'conflict',
    reason,
    entryType: props.sourceRef.entryType,
    entryId: props.sourceRef.entryId,
  }) as const;

const sourceRefKey = (ref: DraftSourceRef) => `${ref.entryType}:${ref.entryId}`;

const resultStatus = (
  action: MatterDraftInvoiceSourceAction,
): 'attached' | 'deleted' | 'removed' =>
  action === 'attach'
    ? 'attached'
    : action === 'delete'
      ? 'deleted'
      : 'removed';

const writeMatterDraftState = (props: {
  transaction: Transaction;
  ref: DocumentReference<MatterDraftInvoiceState>;
  currentState?: MatterDraftInvoiceState;
  nextState: MatterDraftInvoiceState;
}) => {
  if (
    props.nextState.removedSourceRefs.length === 0 &&
    props.nextState.manualAdjustments.length === 0
  ) {
    if (props.currentState) props.transaction.delete(props.ref);
    return { status: 'cleared' } as const;
  }

  props.transaction.set(props.ref, cleanDataToFirestore(props.nextState));
  return { status: 'updated' } as const;
};
